import React from "react";
import PropTypes from "prop-types";
import {
  MdClose,
  MdArticle,
  MdPerson,
  MdCalendarToday,
  MdLocalOffer,
  MdCategory,
  MdAccessTime,
  MdLink,
} from "react-icons/md";

const ActualityDetails = ({ actuality, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "news":
        return "Actualités";
      case "announcement":
        return "Annonces";
      case "event":
        return "Événements";
      case "press":
        return "Presse";
      default:
        return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "news":
        return "text-blue-600 bg-blue-100";
      case "announcement":
        return "text-yellow-600 bg-yellow-100";
      case "event":
        return "text-purple-600 bg-purple-100";
      case "press":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const renderTags = (tags) => {
    if (!tags) return null;

    // Si tags est un array, sinon le splitter
    const tagsArray = Array.isArray(tags) ? tags : tags.split(",");

    return tagsArray.map((tag, index) => (
      <span
        key={index}
        className="mb-2 mr-2 inline-block rounded-full bg-[#2ca01c]/10 px-3 py-1 text-sm text-[#2ca01c]"
      >
        {typeof tag === "string" ? tag.trim() : tag}
      </span>
    ));
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* En-tête */}
      <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-[#333333]">
          Détails de l'actualité
        </h2>
        <button
          onClick={onClose}
          className="rounded-full bg-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-300"
          aria-label="Fermer"
        >
          <MdClose size={20} />
        </button>
      </div>

      {/* Image principale */}
      {actuality.image && (
        <div className="mb-6 overflow-hidden rounded-lg">
          <img
            src={actuality.image}
            alt={actuality.title}
            className="h-64 w-full object-cover md:h-96"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}

      {/* Métadonnées */}
      <div className="mb-6 rounded-lg bg-[#f9f9f9] p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-center text-gray-700">
            <MdPerson className="mr-3 text-xl text-[#7028a9]" />
            <div>
              <span className="font-semibold">Auteur:</span>
              <span className="ml-2">{actuality.author}</span>
            </div>
          </div>

          <div className="flex items-center text-gray-700">
            <MdCalendarToday className="mr-3 text-xl text-[#7028a9]" />
            <div>
              <span className="font-semibold">Date de publication:</span>
              <span className="ml-2">{formatDate(actuality.date)}</span>
            </div>
          </div>

          <div className="flex items-center text-gray-700">
            <MdCategory className="mr-3 text-xl text-[#7028a9]" />
            <div>
              <span className="font-semibold">Catégorie:</span>
              <span className="ml-2">{actuality.category}</span>
            </div>
          </div>

          <div className="flex items-center text-gray-700">
            <MdArticle className="mr-3 text-xl text-[#7028a9]" />
            <div>
              <span className="font-semibold">Type:</span>
              <span
                className={`ml-2 rounded-full px-3 py-1 text-sm font-medium ${getTypeColor(
                  actuality.type
                )}`}
              >
                {getTypeLabel(actuality.type)}
              </span>
            </div>
          </div>

          {/* Slug */}
          {actuality.slug && (
            <div className="flex items-center text-gray-700 md:col-span-2">
              <MdLink className="mr-3 text-xl text-[#7028a9]" />
              <div>
                <span className="font-semibold">Slug:</span>
                <span className="ml-2 rounded bg-gray-100 px-2 py-1 font-mono text-sm">
                  {actuality.slug}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Titre et description */}
      <div className="mb-6">
        <h1 className="mb-4 text-3xl font-bold leading-tight text-[#333333]">
          {actuality.title}
        </h1>
        {actuality.description && (
          <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-lg font-medium text-blue-800">
              {actuality.description}
            </p>
          </div>
        )}
      </div>

      {/* Contenu principal */}
      <div className="mb-6">
        <h3 className="mb-4 flex items-center text-xl font-semibold text-[#333333]">
          <MdArticle className="mr-2 text-[#7028a9]" />
          Contenu de l'article
        </h3>
        <div className="prose prose-lg max-w-none">
          <div className="whitespace-pre-wrap leading-relaxed text-gray-700">
            {actuality.content}
          </div>
        </div>
      </div>

      {/* Tags */}
      {actuality.tags && (
        <div className="mb-6 rounded-lg bg-green-50 p-4">
          <h4 className="mb-3 flex items-center text-lg font-semibold text-green-800">
            <MdLocalOffer className="mr-2" />
            Mots-clés
          </h4>
          <div className="flex flex-wrap">{renderTags(actuality.tags)}</div>
        </div>
      )}

      {/* Footer avec informations supplémentaires */}
      <div className="border-t border-gray-200 pt-6 text-sm text-gray-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <MdAccessTime className="mr-2" />
            <span>
              Publié le {formatDate(actuality.date)} par {actuality.author}
            </span>
          </div>
          <div className="text-right">
            <p>Centre Hospitalier Universitaire Tambohobe</p>
            {actuality.slug && (
              <p className="mt-1 text-xs">ID: {actuality.slug}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
ActualityDetails.propTypes = {
  actuality: PropTypes.shape({
    image: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    category: PropTypes.string,
    type: PropTypes.string,
    slug: PropTypes.string,
    tags: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ActualityDetails;
